<template>
  <div class="app-searchable-select" :class="rootClasses">
    <vue-select
      class="app-searchable-select__field"
      :class="{ 'app-searchable-select__field--multi': multiple }"
      :value="value"
      :options="effectiveOptions"
      :reduce="reduceValue"
      :get-option-label="getOptionLabel"
      :clearable="clearable"
      :searchable="searchable"
      :filterable="filterable"
      :loading="loading"
      :disabled="disabled"
      :placeholder="placeholder"
      :multiple="multiple"
      :append-to-body="appendToBody"
      :calculate-position="appendToBody ? withPopper : undefined"
      label="label"
      @input="onInput"
      @search="onSearch"
    >
      <!-- Swap vue-select's default SVG chevron for the FontAwesome icon
           used everywhere else in the app — matches `app-select` visually. -->
      <template #open-indicator="{ attributes }">
        <span v-bind="attributes" class="app-searchable-select__caret">
          <i class="fa-solid fa-chevron-down"></i>
        </span>
      </template>

      <!-- Contextual empty-state: distinguishes "you've picked everything"
           from "search yielded nothing" so the user knows which lever to
           pull to recover (remove a chip vs change the query). -->
      <template #no-options="{ search }">
        <div class="app-searchable-select__no-options">
          <i
            class="app-searchable-select__no-options-icon fa-regular"
            :class="emptyIcon(search)"
            aria-hidden="true"
          ></i>
          <div class="app-searchable-select__no-options-title">
            {{ emptyTitle(search) }}
          </div>
          <div class="app-searchable-select__no-options-hint">
            {{ emptyHint(search) }}
          </div>
        </div>
      </template>
    </vue-select>
  </div>
</template>

<script>
/**
 * Global `<app-searchable-select>` — a type-to-filter dropdown that
 * matches `app-select` visually but is backed by `<vue-select>` so the
 * user can search long option lists (rule of thumb: ~10+ items).
 *
 * Drop-in API match with `<app-select>` where it makes sense:
 *   - props: `value`, `options: [{label, value}]`, `disabled`, `size`,
 *            `placeholder`, `clearable`
 *   - emits: `input` with the picked option's `value`
 *
 * The visual difference (search field vs native picker) is intentional —
 * use this when the user benefits from typing-to-narrow.
 *
 * Remote search: pass `:filterable="false"` and listen to `@search(query,
 * toggle)`. Fetch matching rows server-side, swap `:options`, and drive the
 * `:loading` prop with your in-flight state. See OrganizationEditModal's
 * Limited Admins field for a working example.
 *
 * Implementation notes:
 *
 * - `:reduce="opt => opt.value"` — peels the option object down to its
 *   primitive `value` so consumers can keep using `v-model` with a plain
 *   string or number, identical to `<app-select>`.
 *
 * - `null → ''` mapping in `onInput` — vue-select emits `null` when the
 *   selection is cleared (or in some edge cases when options change while
 *   open). Mapping back to `''` keeps the contract identical to native
 *   `<select>` behavior.
 *
 * - Width is fixed via SCSS (`min-width`) so the control doesn't visually
 *   collapse when the popover opens. vue-select repositions `.vs__selected`
 *   to `position: absolute` while searching, which would otherwise shrink
 *   the toggle to just the chevron. A min-width is simpler and more
 *   predictable than measuring + locking via JS.
 */
export default {
  name: 'AppSearchableSelect',

  props: {
    // Accepts a primitive for single-select OR an array of primitives for
    // `multiple` mode. Empty selection is `''` (single) / `[]` (multi).
    value: {
      type: [String, Number, Array],
      default: '',
    },
    options: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    // Show the X-button to clear the current selection. Default off
    // because most consumers ship an "All X" sentinel option that acts
    // as the reset state.
    clearable: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    // Client-side filtering of `options` by the typed query. Default on.
    // Set to `false` for REMOTE search: the consumer listens to `@search`,
    // fetches matching rows from the API, and swaps `options`. Local
    // filtering must be off in that mode because vue-select would otherwise
    // filter the fresh server results by `label` (the name) and hide rows
    // that matched on a hidden field like email.
    filterable: {
      type: Boolean,
      default: true,
    },
    // Show vue-select's built-in spinner. Drive it from the consumer's
    // in-flight state during a remote `@search` fetch.
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // Multi-select mode — value becomes an array of reduced primitives,
    // selected options render as removable chips inside the toggle.
    multiple: {
      type: Boolean,
      default: false,
    },
    // Teleport the dropdown menu to <body> so it escapes parent overflow /
    // stacking contexts. Use when the select lives inside a card stack or
    // modal where the dropdown would otherwise be clipped by a sibling.
    appendToBody: {
      type: Boolean,
      default: false,
    },
    // Mirrors app-select's sizes for visual consistency.
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
  },

  emits: ['input', 'search'],

  computed: {
    rootClasses() {
      return [`app-searchable-select--${this.size}`]
    },

    // Stable `{ value → label }` lookup built from the full options prop.
    // Used by `getOptionLabel` so chips keep their human-readable label
    // even after the matching option has been pruned from `effectiveOptions`
    // (multi-mode hide-selected behavior).
    optionLookup() {
      const m = {}
      for (const o of this.options || []) m[o.value] = o.label
      return m
    },

    // Pre-filter options for the dropdown menu. Vue-select's `filter` prop
    // is only invoked when there's an active search query — empty search
    // returns the full optionList unchanged. So to hide already-picked
    // items the moment the dropdown opens (no typing required), we have
    // to strip them at the `:options` level here.
    //
    // Chip labels survive because we override `getOptionLabel` to consult
    // `optionLookup` (built from the FULL options array, not the filtered
    // one). When all options are picked the list collapses to `[]` and
    // vue-select renders the `#no-options` slot.
    effectiveOptions() {
      if (!this.multiple || !Array.isArray(this.value)) return this.options
      const picked = new Set(this.value)
      return this.options.filter((opt) => !picked.has(opt.value))
    },

    // True only when the user has picked every available option in multi
    // mode — drives the "All options selected" empty-state copy.
    allOptionsPicked() {
      return (
        this.multiple
        && Array.isArray(this.value)
        && this.options.length > 0
        && this.value.length >= this.options.length
      )
    },
  },

  methods: {
    // vue-select's `mutableValue` is a mixed bag in multi-mode: items
    // initialized from the `value` prop are raw primitives, but newly
    // picked items are stored as option OBJECTS. When vue-select then
    // calls `reduce()` on the whole list to emit a fresh primitive array,
    // a naive `opt.value` extractor returns `undefined` for the primitive
    // entries — corrupting `value` with stray `undefined` slots that
    // render as empty chips (the previous bug: "first chip goes empty
    // after picking a second item").
    //
    // Handle both shapes: pass primitives through unchanged, extract
    // `.value` from objects.
    reduceValue(opt) {
      if (opt && typeof opt === 'object') return opt.value
      return opt
    },

    // Resolve a chip's display label from the full options cache when
    // vue-select can't find a match in `effectiveOptions` (because we
    // pruned already-picked items for the dropdown view).
    //
    // - Option objects: read `.label` directly
    // - Reduced primitives: look up via `optionLookup`
    // - Fallback: stringify so we never render `undefined`
    //
    // Defensive: `null` / `undefined` opt (can happen briefly in multi mode
    // when vue-select's selectedValue re-resolves against filtered options
    // and finds no match) — return '' so the chip renders empty instead of
    // the literal string "undefined".
    getOptionLabel(opt) {
      if (opt == null) return ''
      if (typeof opt === 'object') {
        if (opt.label != null) return opt.label
        // Object without label — fall through to lookup by its `value` key
        // so multi-mode chips that vue-select handed us as bare-primitive
        // wrappers still resolve.
        const k = opt.value
        if (k != null && this.optionLookup[k] != null) return this.optionLookup[k]
        return ''
      }
      const cached = this.optionLookup[opt]
      return cached != null ? cached : String(opt)
    },

    // Single: vue-select emits the reduced primitive (or null on clear).
    //         Map null → '' so consumers can keep their `''`-as-empty contract.
    // Multi:  vue-select emits an array of reduced primitives. Pass through;
    //         empty list comes back as `[]` which is the right empty state.
    onInput(val) {
      if (this.multiple) {
        this.$emit('input', Array.isArray(val) ? val : [])
        return
      }
      this.$emit('input', val == null ? '' : val)
    },

    // Forward vue-select's search event so consumers can run a remote
    // lookup. `toggle` is vue-select's own loading setter — passing it up
    // lets the consumer flip the spinner around its async fetch. Only
    // meaningful when `filterable` is false (remote mode).
    onSearch(query, toggle) {
      this.$emit('search', query, toggle)
    },

    // Three-state empty UI — icon + title + hint specialise the message
    // so the user knows WHY the list is empty and HOW to recover.
    emptyIcon(search) {
      if (this.loading)            return 'fa-spinner'
      if (this.allOptionsPicked)   return 'fa-circle-check'
      if (String(search || '').trim()) return 'fa-magnifying-glass'
      return 'fa-list'
    },

    emptyTitle(search) {
      if (this.loading)            return 'Searching…'
      if (this.allOptionsPicked)   return 'All options selected'
      const q = String(search || '').trim()
      if (q)                       return `No matches for "${q}"`
      // Remote mode with no query yet — options load on type, so guide the
      // user to start typing instead of implying there's nothing to pick.
      if (!this.filterable)        return 'Type to search'
      return 'No options'
    },

    emptyHint(search) {
      if (this.loading)            return ''
      if (this.allOptionsPicked)   return 'Remove a chip to free one up.'
      if (String(search || '').trim()) return 'Try a different keyword.'
      if (!this.filterable)        return 'Start typing to find options.'
      return ''
    },

    // Popper-style position calculation for `appendToBody` mode. vue-select
    // calls this with the dropdown element and the toggle's DOMRect, expects
    // us to mutate styles on `dropdownList` so it sits flush with the toggle.
    // Default behavior matches a position-absolute dropdown anchored under
    // the toggle, width matching the component.
    withPopper(dropdownList, component, { width, top, left }) {
      dropdownList.style.top      = top
      dropdownList.style.left     = left
      dropdownList.style.width    = width
      dropdownList.style.zIndex   = 9999
      dropdownList.style.position = 'absolute'

      // Tag teleported dropdown so global CSS can scope styles to it.
      // Needed for multi-mode hide-already-picked behavior — the teleported
      // <ul> lives at body level outside the component's scoped CSS scope,
      // so `::v-deep` can't reach it. The class below pairs with an unscoped
      // <style> block at the bottom of this file.
      dropdownList.classList.add('app-searchable-select__menu')
      dropdownList.classList.toggle('app-searchable-select__menu--multi', !!this.multiple)
    },
  },
}
</script>

<style lang="scss" scoped>
// Tokens mirror `app-select` so the two controls visually rhyme in a row.
$ass-text:         #2b3a4a;
$ass-text-muted:   #6c7a89;
$ass-text-subtle:  #9aa6b3;
$ass-bg-surface:   #ffffff;
$ass-bg-soft:      #f6f8fa;
$ass-border:       rgba(0, 0, 0, 0.08);
$ass-blue-focus:   #4392d4;
$ass-blue-ring:    rgba(67, 146, 212, 0.16);
$ass-radius-md:    8px;
$ass-radius-lg:    10px;
$ass-duration:     140ms;
$ass-ease:         cubic-bezier(.4, 0, .2, 1);

.app-searchable-select {
  display: inline-flex;
  flex-direction: column;
  min-width: 0;
  // Stable width so opening the popover doesn't collapse the toggle (see
  // comments in the script block). 200px comfortably fits long labels
  // like "All account managers" without forcing inline scroll.
  width: 200px;

  &--sm { width: 160px; }
  &--lg { width: 240px; }

  // ── vue-select repaint ─────────────────────────────────────────────
  // All vue-select internals are addressed through `::v-deep` since the
  // library renders its own markup. Scoped to `.app-searchable-select`
  // so other vue-selects elsewhere in the app (presenter forms, etc.)
  // keep their own visuals.

  ::v-deep .v-select {
    width: 100%;
  }

  ::v-deep .vs__dropdown-toggle {
    height: 36px;
    min-height: 36px;
    padding: 0 32px 0 12px; // right padding for the chevron
    background: $ass-bg-soft;
    border: 0.5px solid $ass-border;
    border-radius: $ass-radius-md;
    cursor: pointer;
    transition:
      background  $ass-duration $ass-ease,
      border-color $ass-duration $ass-ease,
      box-shadow  $ass-duration $ass-ease;

    &:hover {
      background: lighten($ass-bg-soft, 1%);
    }
  }

  &.vs--open ::v-deep .vs__dropdown-toggle,
  ::v-deep .vs__dropdown-toggle:focus-within {
    background: $ass-bg-surface;
    border-color: $ass-blue-focus;
    box-shadow: 0 0 0 3px $ass-blue-ring;
  }

  ::v-deep .vs__selected-options {
    padding: 0;
    flex: 1;
    min-width: 0;
    flex-wrap: nowrap;
    overflow: hidden;
  }

  ::v-deep .vs__selected {
    margin: 0;
    padding: 0;
    color: $ass-text;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ::v-deep .vs__search,
  ::v-deep .vs__search:focus {
    margin: 0;
    padding: 0;
    font-size: 13px;
    color: $ass-text;
    font-family: inherit;
    flex: 1 1 auto;
    min-width: 1px;
  }

  ::v-deep .vs__actions {
    padding: 0;
  }

  // FA chevron — same icon and position as `app-select`'s caret.
  &__caret {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    color: $ass-text-subtle;
    font-size: 10px;
    pointer-events: none;
    transition: color $ass-duration $ass-ease;
  }

  // ── Dropdown panel ──
  ::v-deep .vs__dropdown-menu {
    border: 0.5px solid $ass-border;
    border-radius: $ass-radius-md;
    box-shadow: 0 6px 24px rgba(0, 0, 0, .08);
    padding: 4px;
    margin-top: 4px;
    max-height: 280px;
    background: $ass-bg-surface;
  }

  ::v-deep .vs__dropdown-option {
    font-size: 13px;
    padding: 6px 10px;
    border-radius: 6px;
    color: $ass-text;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ::v-deep .vs__dropdown-option--highlight {
    background: $ass-bg-soft;
    color: $ass-text;
  }

  // ── Multi-select chips ──
  // When `multiple: true`, vue-select adds `.vs--multiple` to the root and
  // renders each selection as a removable pill inside the toggle. We
  // restyle these into rounded blue-tinted chips that match the app's
  // tag/badge language elsewhere (status pills, attendee chips, etc.).
  //
  // Layout: toggle grows vertically to fit chip rows; chips wrap with a
  // small gap; the search input shrinks to fit remaining space inline so
  // the user can type to filter without losing context.
  $ass-chip-blue:      #1f73c1;
  $ass-chip-bg:        rgba(31, 115, 193, 0.08);
  $ass-chip-bg-hover:  rgba(31, 115, 193, 0.14);
  $ass-chip-border:    rgba(31, 115, 193, 0.18);
  $ass-chip-border-h:  rgba(31, 115, 193, 0.32);

  // Empty / few-chips → toggle matches single-select 36px. Once chips can't
  // fit on a single row, the toggle grows vertically so every chip stays
  // visible (preferred over horizontal scroll — no hidden state, no UX
  // friction). `min-height: 36px` enforces baseline parity with inputs.
  ::v-deep .vs--multiple .vs__dropdown-toggle {
    height: auto;
    min-height: 36px;
    padding: 4px 32px 4px 6px;
    align-items: center;
  }

  ::v-deep .vs--multiple .vs__selected-options {
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
    overflow: visible;
  }

  // Slim chip — fits in the 36px single-row baseline. Wrap-line height
  // adds the chip height + row-gap (4px) per extra line.
  ::v-deep .vs--multiple .vs__selected {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    background: $ass-chip-bg;
    color: $ass-chip-blue;
    border: 0.5px solid $ass-chip-border;
    border-radius: 999px;
    padding: 0 4px 0 10px;
    margin: 0;
    height: 24px;
    line-height: 22px;
    font-size: 11.5px;
    font-weight: 500;
    max-width: 220px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 0;
    transition:
      background  $ass-duration $ass-ease,
      border-color $ass-duration $ass-ease;

    &:hover {
      background: $ass-chip-bg-hover;
      border-color: $ass-chip-border-h;
    }
  }

  // X button — explicit dark glyph so the "remove" affordance reads
  // unambiguously against the chip's tinted background. Replaces vue-
  // select's hairline SVG with a Font Awesome times-icon at 100% opacity.
  ::v-deep .vs--multiple .vs__deselect {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    margin-left: 2px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: $ass-text;
    cursor: pointer;
    opacity: 1;
    transition:
      color       $ass-duration $ass-ease,
      background  $ass-duration $ass-ease;

    &:hover {
      color: #000;
      background: rgba(0, 0, 0, 0.08);
    }

    &:focus-visible {
      outline: 2px solid $ass-blue-focus;
      outline-offset: 1px;
    }

    // Replace vue-select's hairline SVG with a Unicode × (multiplication
    // sign) — bolder and more recognizable as a close affordance, with no
    // dependency on an icon font being loaded for the glyph to render.
    svg {
      display: none;
    }

    &::before {
      content: '\00D7'; /* × U+00D7 — multiplication sign */
      font-size: 14px;
      font-weight: 700;
      line-height: 1;
    }
  }

  // Search input shares the row with chips — narrow flex-basis so it
  // doesn't push chips to a new line on first keystroke.
  ::v-deep .vs--multiple .vs__search,
  ::v-deep .vs--multiple .vs__search:focus {
    flex: 1 1 80px;
    min-width: 80px;
    padding: 2px 4px;
  }

  // Already-picked options hide from the dropdown menu (multi mode only).
  // The options array stays unchanged in the component prop — vue-select
  // needs it intact to resolve chip labels — and we just suppress the
  // already-selected entries visually. Removing a chip drops the
  // `--selected` class from its option, which reappears automatically.
  ::v-deep .vs--multiple .vs__dropdown-option--selected {
    display: none;
  }

  // ── Disabled state ──
  &--disabled,
  ::v-deep .vs--disabled .vs__dropdown-toggle {
    opacity: 0.7;
    cursor: not-allowed;
  }

  // Empty-state inside dropdown — icon + title + hint pattern. Centered
  // so it reads as deliberate "no content" rather than missing data.
  // Targets both inline (scoped via :v-deep) and teleported (unscoped
  // block below) dropdowns.
  ::v-deep .app-searchable-select__no-options {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 20px 16px;
    text-align: center;
    color: $ass-text-muted;
  }
  ::v-deep .app-searchable-select__no-options-icon {
    font-size: 22px;
    color: $ass-text-subtle;
    margin-bottom: 4px;
  }
  ::v-deep .app-searchable-select__no-options-title {
    font-size: 13px;
    font-weight: 600;
    color: $ass-text;
  }
  ::v-deep .app-searchable-select__no-options-hint {
    font-size: 11px;
    color: $ass-text-muted;
    &:empty { display: none; }
  }

  // ── Sizes ── (mirrors app-select's height tiers)
  &--sm ::v-deep .vs__dropdown-toggle {
    height: 28px;
    min-height: 28px;
    padding: 0 26px 0 10px;
    font-size: 12px;
  }
  &--sm &__caret { right: 9px; font-size: 9px; }

  &--lg ::v-deep .vs__dropdown-toggle {
    height: 44px;
    min-height: 44px;
    padding: 0 36px 0 14px;
    border-radius: $ass-radius-lg;
  }
  &--lg ::v-deep .vs__selected,
  &--lg ::v-deep .vs__search { font-size: 14px; }
  &--lg &__caret { right: 14px; font-size: 11px; }
}
</style>

<!--
  Unscoped block for `appendToBody` teleported dropdowns. The <ul> is
  rendered at <body> level and is OUTSIDE this component's scoped CSS
  scope — `::v-deep` can't reach it. Class tags are applied via the
  `withPopper` callback in `methods`.
-->
<style lang="scss">
// Empty-state styling for teleported dropdowns (the `<ul>` lives at body
// level, outside the component's scoped CSS scope).
.app-searchable-select__menu {
  .app-searchable-select__no-options {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 20px 16px;
    text-align: center;
    color: #6c7a89;
  }

  .app-searchable-select__no-options-icon {
    font-size: 22px;
    color: #9aa6b3;
    margin-bottom: 4px;
  }

  .app-searchable-select__no-options-title {
    font-size: 13px;
    font-weight: 600;
    color: #2b3a4a;
  }

  .app-searchable-select__no-options-hint {
    font-size: 11px;
    color: #6c7a89;

    &:empty { display: none; }
  }
}
</style>
